/**
 * Registration Step 5 - Emergency Contact Form (v2)
 *
 * Migrated to reusable wizard architecture.
 * - Receives data via props (registrationData)
 * - Updates parent via callbacks (onDataChange, onValidationChange)
 * - No context dependency - pure component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FormInput } from '../../FormInput';
import { PhoneNumberInput } from '../../PhoneNumberInput';
import styles from './Step6EmergencyContactForm.module.css';

const Step5EmergencyContactForm = ({
  registrationData,
  onDataChange,
  onValidationChange
}) => {
  const data = registrationData.emergency_contact;
  const parent1 = registrationData.parent1;

  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // Validation logic
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Check if at least one emergency contact is selected
    const hasParentSelected = data.parent_emergency_contacts &&
      Object.values(data.parent_emergency_contacts).some(selected => selected);
    const hasAdditionalContact = data.use_additional_contact &&
      data.additional_emergency_contact?.name &&
      data.additional_emergency_contact?.phone &&
      data.additional_emergency_contact?.relationship;

    if (!hasParentSelected && !hasAdditionalContact) {
      newErrors.emergency_contact = 'At least one emergency contact must be designated';
    }

    // Validate additional contact fields if that option is selected
    if (data.use_additional_contact) {
      if (!data.additional_emergency_contact?.name?.trim()) {
        newErrors['additional_emergency_contact.name'] = 'Emergency contact name is required';
      }
      if (!data.additional_emergency_contact?.phone?.trim()) {
        newErrors['additional_emergency_contact.phone'] = 'Emergency contact phone is required';
      }
      if (!data.additional_emergency_contact?.relationship?.trim()) {
        newErrors['additional_emergency_contact.relationship'] = 'Emergency contact relationship is required';
      }
    }

    setErrors(newErrors);
    const formIsValid = Object.keys(newErrors).length === 0;
    setIsValid(formIsValid);

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(formIsValid);
    }

    return formIsValid;
  }, [data, onValidationChange]);

  // Validate on data change
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  // Handle parent checkbox changes
  const handleParentCheckbox = useCallback((parentKey, checked) => {
    setIsDirty(true);

    onDataChange({
      ...registrationData,
      emergency_contact: {
        ...data,
        parent_emergency_contacts: {
          ...data.parent_emergency_contacts,
          [parentKey]: checked
        }
      }
    });
  }, [data, registrationData, onDataChange]);

  // Handle additional contact toggle
  const handleAdditionalContactToggle = useCallback((checked) => {
    setIsDirty(true);

    onDataChange({
      ...registrationData,
      emergency_contact: {
        ...data,
        use_additional_contact: checked,
        additional_emergency_contact: checked ? data.additional_emergency_contact : {
          name: '',
          phone: '',
          relationship: ''
        }
      }
    });
  }, [data, registrationData, onDataChange]);

  // Handle additional contact field changes
  const handleAdditionalContactChange = useCallback((e) => {
    const { name, value } = e.target;
    setIsDirty(true);

    onDataChange({
      ...registrationData,
      emergency_contact: {
        ...data,
        additional_emergency_contact: {
          ...data.additional_emergency_contact,
          [name]: value
        }
      }
    });
  }, [data, registrationData, onDataChange]);

  return (
    <div>
      <div className={styles.formHeader}>
        <h3 className={styles.formTitle}>
          Emergency Contact Information
        </h3>
        <p className={styles.formSubtitle}>
          Designate at least one emergency contact
        </p>
        {isDirty && !isValid && (
          <div className={styles.validationWarning}>
            Please select at least one emergency contact
          </div>
        )}
      </div>

      {/* Parent Emergency Contact Options */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          Parents/Guardians
        </h4>

        {/* Parent 1 Checkbox */}
        {parent1 && parent1.first_name && (
          <div className={styles.checkboxCard}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={!!data.parent_emergency_contacts?.parent1}
                onChange={e => handleParentCheckbox('parent1', e.target.checked)}
                className={styles.checkboxInput}
              />
              <div>
                <div className={styles.parentName}>
                  {parent1.first_name} {parent1.last_name}
                </div>
                <div className={styles.parentDetails}>
                  {parent1.relationship_to_player} • {parent1.phone}
                </div>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Additional Emergency Contact Option */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionHeading}>
          Additional Emergency Contact
        </h4>

        <div className={styles.checkboxCard}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!data.use_additional_contact}
              onChange={e => handleAdditionalContactToggle(e.target.checked)}
              className={styles.checkboxInput}
            />
            Add additional emergency contact
          </label>
        </div>

        {/* Additional Contact Form Fields */}
        {data.use_additional_contact && (
          <div className={styles.additionalContactForm}>
            <h5 className={styles.additionalContactHeading}>
              Emergency Contact Details
            </h5>

            <FormInput
              label="Full Name"
              name="name"
              value={data.additional_emergency_contact?.name || ''}
              onChange={handleAdditionalContactChange}
              error={errors['additional_emergency_contact.name']}
              required
              placeholder="Contact's full name"
            />

            <PhoneNumberInput
              label="Phone Number"
              name="phone"
              value={data.additional_emergency_contact?.phone || ''}
              onChange={handleAdditionalContactChange}
              error={errors['additional_emergency_contact.phone']}
              required
              placeholder="(555) 123-4567"
            />

            <FormInput
              label="Relationship to Player"
              name="relationship"
              value={data.additional_emergency_contact?.relationship || ''}
              onChange={handleAdditionalContactChange}
              error={errors['additional_emergency_contact.relationship']}
              required
              placeholder="Grandparent, Family Friend, etc."
            />
          </div>
        )}
      </div>

      {/* Validation Error Display */}
      {errors.emergency_contact && (
        <div className={styles.errorAlert}>
          {errors.emergency_contact}
        </div>
      )}
    </div>
  );
};

export default Step5EmergencyContactForm;
